/**
 * The one place that talks to the server.
 *
 * Three things live here rather than in the screens:
 *
 * **The token.** Maestro signs in on its own page — no hosted UI, no redirect
 * (docs/06, decision 2) — so the browser holds an access token and a refresh
 * token. A 401 on any call means the access token has expired, so the client
 * refreshes once and retries. Every screen would otherwise have to handle that,
 * and would handle it differently.
 *
 * **"Could not load" versus "no permission" versus "signed out".** Screen 18
 * draws a different thing for each, so the error carries the status rather than
 * a message the screen has to read.
 *
 * **Connection lost.** When a call cannot reach the server at all, that is not
 * the same as a failure — the screen keeps the last data it had and turns the
 * launch buttons off (screen 18), so the client says which it was.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** True when the server could not be reached at all. */
    readonly offline = false
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get signedOut(): boolean {
    return this.status === 401;
  }
  get notAllowed(): boolean {
    return this.status === 403;
  }
  get missing(): boolean {
    return this.status === 404;
  }
}

const ACCESS = 'maestro.access_token';
const REFRESH = 'maestro.refresh_token';
/** Only for local development against AUTH_MODE=dev. */
const DEV_USER = 'maestro.dev_user';
const DEV_HEADER = 'x-maestro-dev-user';

export const session = {
  get access(): string | null {
    return localStorage.getItem(ACCESS);
  },
  get refresh(): string | null {
    return localStorage.getItem(REFRESH);
  },
  get devUser(): string | null {
    return localStorage.getItem(DEV_USER);
  },
  hold(tokens: { access_token: string; refresh_token?: string }): void {
    localStorage.setItem(ACCESS, tokens.access_token);
    if (tokens.refresh_token) localStorage.setItem(REFRESH, tokens.refresh_token);
  },
  /** Sign in as somebody in the sample data, for local work only. */
  holdDevUser(sub: string): void {
    localStorage.setItem(DEV_USER, sub);
  },
  clear(): void {
    localStorage.removeItem(ACCESS);
    localStorage.removeItem(REFRESH);
    localStorage.removeItem(DEV_USER);
  },
  get signedIn(): boolean {
    return this.access != null || this.devUser != null;
  }
};

/** Everybody who wants to know the session ended, so the app can show one page. */
const signedOutListeners = new Set<() => void>();
export function onSignedOut(listener: () => void): () => void {
  signedOutListeners.add(listener);
  return () => signedOutListeners.delete(listener);
}

function headers(extra?: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = { ...extra };
  const token = session.access;
  if (token) out.authorization = `Bearer ${token}`;
  // In development the server trusts this header instead (AUTH_MODE=dev). It
  // is never sent when there is a real token.
  else if (session.devUser) out[DEV_HEADER] = session.devUser;
  return out;
}

async function refreshOnce(): Promise<boolean> {
  const token = session.refresh;
  if (!token) return false;
  try {
    const res = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refresh_token: token })
    });
    if (!res.ok) return false;
    const body = (await res.json()) as { access_token?: string };
    if (!body.access_token) return false;
    session.hold({ access_token: body.access_token });
    return true;
  } catch {
    return false;
  }
}

async function readError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string; message?: string };
    return body.error ?? body.message ?? `The server answered ${res.status}.`;
  } catch {
    return `The server answered ${res.status}.`;
  }
}

async function call<T>(
  method: string,
  path: string,
  body?: unknown,
  retrying = false,
  /**
   * True for the endpoints you reach *before* you have a session: sign in,
   * answering the new-password challenge, refreshing. A 401 from one of
   * those means the credentials were wrong, not that a session ended, and
   * there is no session to clear or to refresh and retry.
   */
  unauthenticated = false
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers: headers(body === undefined ? undefined : { 'content-type': 'application/json' }),
      ...(body === undefined ? {} : { body: JSON.stringify(body) })
    });
  } catch {
    // Not a failure of the request: the server could not be reached. Screen 18
    // keeps the last data and turns the launch buttons off.
    throw new ApiError(0, 'Maestro cannot be reached. Showing the last thing it said.', true);
  }

  if (res.status === 401 && !retrying && !unauthenticated) {
    // The access token expired mid-session. Refresh once and try again, so
    // nobody is thrown back to the sign-in page while they are working.
    if (await refreshOnce()) return call<T>(method, path, body, true);
    session.clear();
    for (const listener of signedOutListeners) listener();
    throw new ApiError(401, 'Your session has ended. Sign in again.');
  }

  if (!res.ok) throw new ApiError(res.status, await readError(res));
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** A query string from a plain object, leaving out anything unset. */
export function query(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === '') continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text === '' ? '' : `?${text}`;
}

export const api = {
  get: <T>(path: string): Promise<T> => call<T>('GET', path),
  post: <T>(path: string, body?: unknown): Promise<T> => call<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body?: unknown): Promise<T> => call<T>('PATCH', path, body ?? {}),
  put: <T>(path: string, body?: unknown): Promise<T> => call<T>('PUT', path, body ?? {}),
  del: <T>(path: string): Promise<T> => call<T>('DELETE', path)
};

/** Sign in with an email and a password, on Maestro's own page. */
export async function signIn(
  email: string,
  password: string
): Promise<
  | { ok: true }
  | { ok: false; newPasswordRequired: true; session: string }
  | { ok: false; why: string }
> {
  try {
    const body = await call<{
      access_token?: string;
      refresh_token?: string;
      challenge?: string;
      session?: string;
    }>('POST', '/api/auth/sign-in', { email, password }, false, true);

    if (body.challenge === 'NEW_PASSWORD_REQUIRED' && body.session) {
      return { ok: false, newPasswordRequired: true, session: body.session };
    }
    if (!body.access_token) return { ok: false, why: 'Maestro did not return a session.' };
    session.hold({
      access_token: body.access_token,
      ...(body.refresh_token ? { refresh_token: body.refresh_token } : {})
    });
    return { ok: true };
  } catch (err) {
    return { ok: false, why: err instanceof ApiError ? err.message : 'Sign-in failed.' };
  }
}

export async function chooseNewPassword(
  email: string,
  password: string,
  cognitoSession: string
): Promise<{ ok: true } | { ok: false; why: string }> {
  try {
    const body = await call<{ access_token?: string; refresh_token?: string }>(
      'POST',
      '/api/auth/new-password',
      { email, password, session: cognitoSession },
      false,
      true
    );
    if (!body.access_token) return { ok: false, why: 'Maestro did not return a session.' };
    session.hold({
      access_token: body.access_token,
      ...(body.refresh_token ? { refresh_token: body.refresh_token } : {})
    });
    return { ok: true };
  } catch (err) {
    return { ok: false, why: err instanceof ApiError ? err.message : 'That password was refused.' };
  }
}
